import { createAuthClient } from "better-auth/react";
import { magicLinkClient } from "better-auth/client/plugins";
import { resolveApiUrl } from "./apiOrigin";

/**
 * API origin. Production pages call api.<this host> so the session cookie stays same-site.
 * Dev: the API on :3000 — localhost cookies are shared across ports.
 */
export const API_URL: string = resolveApiUrl({
  dev: import.meta.env.DEV,
  baked: import.meta.env.VITE_API_URL,
  hostname: typeof window === "undefined" ? undefined : window.location.hostname,
});

export const authClient = createAuthClient({
  baseURL: API_URL || window.location.origin,
  basePath: "/api/auth",
  plugins: [magicLinkClient()],
});

export type SessionData = typeof authClient.$Infer.Session;

export async function fetchMe(): Promise<Me | null> {
  const res = await fetch(`${API_URL}/me`, { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`me: ${res.status}`);
  return (await res.json()) as Me;
}

export interface Me {
  userId: string;
  isAnonymous: boolean;
  email: string | null;
  group: "guest" | "global" | "hey_church" | "leader" | "pastor";
  locale: "de" | "en";
  isSuperadmin: boolean;
  profile: { alias: string; fullName: string | null; lang: "de" | "en"; onboardingDone: boolean };
  membership: { churchId: string; role: "member" | "leader" | "pastor" | "admin"; active: boolean } | null;
  church: { id: string; name: string; slug: string; status: string } | null;
}

/** Command Center access: active pastor/admin of a church, or a superadmin. */
export function canAccessCommandCenter(me: Me | null): boolean {
  if (!me) return false;
  if (me.isSuperadmin) return true;
  return Boolean(me.membership?.active && (me.membership.role === "pastor" || me.membership.role === "admin"));
}
